import { ComponentType, ReactElement, useEffect, useState } from 'react';

import { PopoverProps } from '@mui/material';
import { usePopupState } from 'material-ui-popup-state/hooks';

import { DropdownInput } from './components/DropdownInput';
import { DropdownPopper } from './components/DropdownPopper';
import { DropdownRoot } from './components/DropdownRoot';
import { DropdownContext } from './DropdownContext';
import { DropdownProps } from './types';
import { useDropdownWithAutocomplete } from './useDropdownWithAutocomplete';
import { toLegacySize } from './utils';
import { WithRef } from '../../../types';
import { ListContext } from '../../lists/List';

export type SelectProps<
  OptionItem = unknown,
  OptionValue = OptionItem,
  SelectionType extends 'single' | 'multiple' | undefined = undefined
> = DropdownProps<OptionItem, OptionValue, SelectionType> & {
  PopoverComponent?: ComponentType<PopoverProps>;
};

export const Select = (({
  ref,
  PopoverComponent,
  ...dropdownProps
}: WithRef<HTMLInputElement, SelectProps>) => {
  const popupState = usePopupState({
    variant: 'popover',
    popupId: 'dropdown',
  });
  const isSingleSelection = dropdownProps.multiple !== true;
  const [inputValue, setInputValue] = useState('');
  const autocomplete = useDropdownWithAutocomplete({
    dropdownProps,
    autocompleteOverrides: {
      onChange: (option) => {
        if (isSingleSelection) {
          if (option) {
            const valueResult =
              autocomplete.getters.getOptionSelectedValue(option);
            // Check if not a ReactNode
            setInputValue(typeof valueResult === 'string' ? valueResult : '');
          } else {
            setInputValue('');
          }

          dropdownProps.onChange?.(
            option,
            autocomplete.getters.getOptionValue(option)
          );
        }
      },
      open: popupState.isOpen,
      hideSearch: true,
    },
  });

  useEffect(() => {
    if (isSingleSelection) {
      const valueResult = autocomplete.states.selectedOption
        ? autocomplete.getters.getOptionSelectedValue(
            autocomplete.states.selectedOption
          )
        : '';
      // Check if not a ReactNode
      setInputValue(typeof valueResult === 'string' ? valueResult : '');
    }
  }, [
    isSingleSelection,
    autocomplete.getters,
    autocomplete.states.selectedOption,
  ]);

  return (
    <DropdownContext.Provider
      value={{
        popupState,
        dropdownProps,
        autocomplete,
      }}
    >
      <ListContext.Provider value={{ size: toLegacySize(dropdownProps.size) }}>
        <DropdownRoot>
          <DropdownInput
            inputProps={{
              value: inputValue,
            }}
            ref={ref}
          />
        </DropdownRoot>

        <DropdownPopper PopoverComponent={PopoverComponent} />
      </ListContext.Provider>
    </DropdownContext.Provider>
  );
}) as (<
  OptionItem,
  OptionValue = OptionItem,
  SelectionType extends 'single' | 'multiple' | undefined = undefined
>(
  props: WithRef<
    HTMLInputElement,
    SelectProps<OptionItem, OptionValue, SelectionType>
  >
) => ReactElement) & {
  displayName: string;
};

Select.displayName = 'Select';
